import {
  Sun,
  Moon,
  Monitor,
  Palette,
  ChevronUp,
  PenTool
} from "lucide-react"
import { 
  Gauge, 
  Camera,
  YoutubeLogo,
  FileDoc,
  IconWeight
} from "phosphor-react"
import { Link } from "react-router-dom"
import { useState, useRef, useEffect } from "react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { PageType } from "@/types"

type ThemeMode = 'light' | 'dark' | 'auto';

interface AppSidebarProps {
  currentPage: PageType;
  onPageChange?: (page: PageType) => void;
  themeMode: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  isDarkMode: boolean;
}

// Menu items adaptados para o projeto ConverterPro com ícones modernos
const navigationItems = [
  {
    id: 'home' as const,
    title: 'Dashboard',
    path: '/',
    icon: Gauge,
    iconColor: 'text-fuchsia-500 dark:text-fuchsia-400',
    activeColor: 'text-fuchsia-600 dark:text-fuchsia-400 drop-shadow-[0_0_8px_rgba(192,38,211,0.5)]',
    weight: 'duotone' as IconWeight
  },
  {
    id: 'images' as const,
    title: 'Conversor de Imagens',
    path: '/images',
    icon: Camera,
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    activeColor: 'text-cyan-600 dark:text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]',
    weight: 'duotone' as IconWeight
  },
  {
    id: 'videos' as const,
    title: 'Conversor de Vídeos',
    path: '/videos',
    icon: YoutubeLogo,
    iconColor: 'text-rose-500 dark:text-rose-400',
    activeColor: 'text-rose-600 dark:text-rose-400 drop-shadow-[0_0_8px_rgba(244,63,94,0.5)]',
    weight: 'duotone' as IconWeight
  },
  {
    id: 'documents' as const,
    title: 'Conversor de Documentos',
    path: '/documents',
    icon: FileDoc,
    iconColor: 'text-blue-500 dark:text-blue-400',
    activeColor: 'text-blue-600 dark:text-blue-400 drop-shadow-[0_0_8px_rgba(37,99,235,0.5)]',
    weight: 'duotone' as IconWeight
  },
  {
    id: 'copywriter' as const,
    title: 'Agente CopyWriter',
    path: '/copywriter',
    icon: PenTool,
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    activeColor: 'text-emerald-600 dark:text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]',
    weight: 'duotone' as IconWeight
  }
];

export function AppSidebar({ 
  currentPage, 
  themeMode, 
  onThemeChange, 
  isDarkMode 
}: AppSidebarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setOpenMobile } = useSidebar();

  // Função para fechar sidebar mobile após navegação
  const handleNavigationClick = () => {
    // Fechar sidebar mobile após um pequeno delay para permitir a navegação
    setTimeout(() => {
      setOpenMobile(false);
    }, 100);
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getThemeIcon = () => {
    switch (themeMode) {
      case 'light': return Sun;
      case 'dark': return Moon;
      case 'auto': return Monitor;
      default: return Palette;
    }
  };

  const getThemeLabel = () => {
    switch (themeMode) {
      case 'light': return 'Claro';
      case 'dark': return 'Escuro';
      case 'auto': return 'Sistema';
      default: return 'Tema';
    }
  };

  const getThemeDescription = () => {
    switch (themeMode) {
      case 'light': return 'Interface clara e limpa';
      case 'dark': return 'Interface escura e elegante';
      case 'auto': return 'Interface do seu sistema operacional';
      default: return 'Escolha um tema de sua preferência';
    }
  };

  const handleThemeSelect = (theme: ThemeMode) => {
    onThemeChange(theme);
    setIsDropdownOpen(false);
  };

  const themeOptions = [
    {
      id: 'light' as const,
      label: 'Claro',
      description: 'Interface clara e limpa',
      icon: Sun,
      color: 'blue'
    },
    {
      id: 'dark' as const,
      label: 'Escuro',
      description: 'Interface escura e elegante',
      icon: Moon,
      color: 'purple'
    },
    {
      id: 'auto' as const,
      label: 'Sistema',
      description: 'Seguir configuração do sistema',
      icon: Monitor,
      color: 'green'
    }
  ];

  return (
    <Sidebar className="w-72">
      {/* Header com Logo */}
      <SidebarHeader className="border-b px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="relative w-8 h-8 flex-shrink-0">
            <div className="relative w-8 h-8">
              {/* Núcleo central - posicionamento fixo sem transform */}
              <div className={`absolute top-2 left-2 w-4 h-4 rounded-full shadow-lg ${
                isDarkMode 
                  ? 'bg-purple-500 shadow-purple-500/30'
                  : 'bg-purple-700 shadow-purple-700/40'
              }`}></div>
              {/* Anel externo - posicionamento absoluto fixo */}
              <div className={`absolute inset-0 w-8 h-8 border border-transparent rounded-full ${
                isDarkMode 
                  ? 'border-t-cyan-400/40 border-r-blue-500/40' 
                  : 'border-t-cyan-600/40 border-r-blue-700/40'
              }`}></div>
              {/* Anel interno - posicionamento absoluto fixo */}
              <div className={`absolute top-1 left-1 w-6 h-6 border border-transparent rounded-full ${
                isDarkMode 
                  ? 'border-b-purple-600/40 border-l-cyan-400/40' 
                  : 'border-b-purple-800/40 border-l-cyan-600/40'
              }`}></div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className={`text-xl font-bold leading-tight ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
              ConverterPro
            </h1>
            <p className="text-sm text-muted-foreground leading-tight">by Tech Solutions Pro ©</p>
          </div>
        </div>
      </SidebarHeader>
      
      {/* Conteúdo Principal */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navegação</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => {
                const isActive = currentPage === item.id;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton 
                      asChild
                      isActive={isActive}
                    >
                      <Link
                        to={item.path}
                        onClick={handleNavigationClick}
                        className={`w-full flex items-center justify-start space-x-3 px-3 py-2 transition-all duration-200 group text-[14px] leading-5 font-normal ${
                          isActive 
                            ? 'md:text-blue-400' 
                            : 'text-muted-foreground md:hover:text-blue-400'
                        }`}
                      >
                        <item.icon 
                          className={`w-5 h-5 transition-all duration-300 ${
                            isActive 
                              ? item.activeColor 
                              : `${item.iconColor} group-hover:scale-110 group-hover:drop-shadow-[0_0_4px_rgba(var(--icon-glow),0.5)]`
                          }`} 
                          weight={item.weight}
                          style={{'--icon-glow': isActive ? '255,255,255' : '0,0,0'} as React.CSSProperties}
                        />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      {/* Footer com Seletor de Tema Dropdown */}
      <SidebarFooter className="border-t px-4 py-3">
        <div className="relative" ref={dropdownRef}>
          {/* Dropdown Menu (Mobile/Tablet e Desktop) */}
          <div className={`absolute bottom-[calc(100%+0.5rem)] inset-x-0 transition-all duration-[2000ms] ease-in-out transform ${
            isDropdownOpen
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}>
            <div className={`p-1.5 rounded-lg border shadow-lg ${
              isDarkMode 
                ? 'bg-slate-800 border-slate-700 shadow-black/30' 
                : 'bg-white border-gray-200 shadow-gray-900/10'
            }`}>
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = themeMode === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => handleThemeSelect(option.id)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-all duration-200 ${
                      isDarkMode 
                        ? 'hover:bg-slate-700/50 text-slate-200' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-[18px] h-[18px] ${
                        option.id === 'light'
                          ? 'text-blue-500'
                          : option.id === 'dark'
                          ? 'text-purple-500'
                          : 'text-green-500'
                      }`} />
                      <div className="text-left">
                        <p className="text-[13px] font-medium leading-none mb-1">{option.label}</p>
                        <p className="text-[11px] text-muted-foreground leading-none">{option.description}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className={`w-1.5 h-1.5 rounded-full bg-green-500`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Botão do Dropdown (Visível apenas em mobile/tablet) */}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full lg:hidden flex items-center justify-between p-2.5 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'hover:bg-slate-800/50 text-slate-200' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <div className="flex items-center gap-3">
              {(() => {
                const Icon = getThemeIcon();
                return (
                  <Icon className={`w-[18px] h-[18px] ${
                    themeMode === 'light'
                      ? 'text-blue-500'
                      : themeMode === 'dark'
                      ? 'text-purple-500'
                      : 'text-green-500'
                  }`} />
                );
              })()}
              <div className="text-left">
                <p className="text-[13px] font-medium leading-none mb-1">{getThemeLabel()}</p>
                <p className="text-[11px] text-muted-foreground leading-none">{getThemeDescription()}</p>
              </div>
            </div>
            <ChevronUp 
              className={`w-4 h-4 transition-transform duration-300 ${
                isDropdownOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>

          {/* Botão do Dropdown (Visível apenas em desktop) */}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full hidden lg:flex items-center justify-between p-2.5 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'hover:bg-slate-800/50 text-slate-200' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <div className="flex items-center gap-3">
              {(() => {
                const Icon = getThemeIcon();
                return (
                  <Icon className={`w-[18px] h-[18px] ${
                    themeMode === 'light'
                      ? 'text-blue-500'
                      : themeMode === 'dark'
                      ? 'text-purple-500'
                      : 'text-green-500'
                  }`} />
                );
              })()}
              <div className="text-left">
                <p className="text-[13px] font-medium leading-none mb-1">{getThemeLabel()}</p>
                <p className="text-[11px] text-muted-foreground leading-none">{getThemeDescription()}</p>
              </div>
            </div>
            <ChevronUp 
              className={`w-4 h-4 transition-transform duration-300 ${
                isDropdownOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
} 